"use client";

import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { Images, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAdminOverlay } from "./admin-overlay-context";

// Déclenche le job de fond (Stitch) qui génère une maquette par enjeu — même
// route que le bouton "Générer les maquettes des enjeux" de l'admin classique.
// Contrairement aux autres sections, ça ne se termine pas dans la requête : on
// relance `router.refresh()` toutes les quelques secondes tant que
// `data.stakesMockups.status` reste "running"/"queued", pour voir les images
// apparaître sans recharger la page à la main.
export function StakesMockupsRegenerateButton({ className = "" }: { className?: string }) {
  const { itemId, data } = useAdminOverlay();
  const router = useRouter();
  const [isTriggering, setIsTriggering] = useState(false);
  const status = data.stakesMockups?.status;
  const busy = status === "running" || status === "queued";
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (busy && !pollRef.current) {
      pollRef.current = setInterval(() => router.refresh(), 4000);
    }
    if (!busy && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [busy, router]);

  const trigger = async () => {
    setIsTriggering(true);
    try {
      const res = await fetch(`/api/super-admin/content/${itemId}/generate-stakes-mockups`, {
        method: "POST",
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.error || "Échec du déclenchement");
      toast.success("Génération des maquettes des enjeux lancée");
      router.refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Échec du déclenchement");
    } finally {
      setIsTriggering(false);
    }
  };

  return (
    <button
      type="button"
      onClick={trigger}
      disabled={isTriggering || busy}
      title="Régénérer les maquettes des enjeux"
      className={`inline-flex h-8 w-8 items-center justify-center rounded-full border border-line bg-paper-pure text-ink/60 shadow-sm transition-colors hover:border-gold hover:text-gold disabled:opacity-50 ${className}`}
    >
      {isTriggering || busy ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : (
        <Images className="h-3.5 w-3.5" />
      )}
    </button>
  );
}
